import { createAsyncThunk } from '@reduxjs/toolkit';
import { FormikProps } from 'formik';
import container from '../../container';
import {
  ConfirmRecoveryPasswordRequestDTO,
  CreateUserRequestDTO,
  RecoveryPasswordRequestDTO,
  RemoveRequestDTO,
  UserLoginRequestDTO,
  UserLoginResponseDTO,
  UserUpdateRequestDTO,
} from '../../domain/user/user.dtos';
import { User } from '../../domain/user/user.models';
import { UserService } from '../../domain/user/user.service';
import { routes } from '../../routes';
import { addCustomerId, getCart, getCartByCustomerId, removeCart } from '../cart/cart.thunks';
import { DefaultState } from '../store';
import { clearCart, setCustomerId } from '../cart/cart.slice';
import { LocationState } from '../../types';
import { StorageHandlerHelper } from '../../helpers';
import SnackbarAction from '../snackbar/snackbar.actions';
import { AnalyticsAdapter, AnalyticsEventCategory, AnalyticsEventName } from '../../domain/analytics/AnalyticsAdapter';

const userService = container.get<UserService>('UserService');
const analyticsAdapter = container.get<AnalyticsAdapter>('AnalyticsAdapter');

export const login = createAsyncThunk<UserLoginResponseDTO, { userData: UserLoginRequestDTO }, { rejectValue: string }>(
  'user/login',
  async ({ userData }, thunkApi) => {
    try {
      analyticsAdapter.trackEvent({
        action: AnalyticsEventName.USER_LOGIN,
        category: AnalyticsEventCategory.ECOMMERCE,
        params: {
          email: userData.email,
        },
      });
      const response: User = await userService.logIn(userData);
      const state: DefaultState = thunkApi.getState() as DefaultState;

      if (!state.cart?.cart) {
        if (response && response.id) {
          thunkApi.dispatch(getCartByCustomerId({ data: { customerId: response.id } }));
        } else {
          thunkApi.dispatch(getCart());
        }
      } else if (!state.cart?.cart.customerId) {
        await addCustomerId(state.cart.cart.id);
        thunkApi.dispatch(setCustomerId(response.id));
      }
      StorageHandlerHelper.save('token', response.token);
      return response;
    } catch (error) {
      thunkApi.dispatch(SnackbarAction.error((error as Error).message));
      return thunkApi.rejectWithValue((error as Error).message);
    }
  },
);

export const refreshJwt = createAsyncThunk<UserLoginResponseDTO, {}, { rejectValue: string }>(
  'user/refreshJwt',
  async (_, thunkApi) => {
    try {
      const response = await userService.refreshFromJwt();
      if (response && response.id) {
        thunkApi.dispatch(getCartByCustomerId({ data: { customerId: response.id } }));
      }
      return response;
    } catch (error) {
      StorageHandlerHelper.clear('token');
      return thunkApi.rejectWithValue((error as Error).message);
    }
  },
);

export const registerUser = createAsyncThunk<
  {},
  { userData: CreateUserRequestDTO; formik: FormikProps<any> },
  { rejectValue: string }
>('user/register', async ({ userData, formik }, thunkApi) => {
  try {
    analyticsAdapter.trackEvent({
      action: AnalyticsEventName.USER_REGISTER,
      category: AnalyticsEventCategory.ECOMMERCE,
      params: {
        name: userData.name,
        lastName: userData.lastName,
        birthDate: userData.birthDate,
        email: userData.email,
      },
    });
    await userService.create(userData);
    formik.resetForm();
    thunkApi.dispatch(SnackbarAction.success('Registro exitoso, revise su bandeja de entrada o spam'));
  } catch (error) {
    thunkApi.dispatch(SnackbarAction.error((error as Error).message));
    return thunkApi.rejectWithValue((error as Error).message);
  }
});

export const recoveryPassword = createAsyncThunk<
  {},
  { userData: RecoveryPasswordRequestDTO; formik: FormikProps<any> },
  { rejectValue: string }
>('user/recovery-password', async ({ userData, formik }, thunkApi) => {
  try {
    await userService.recoveryPassword(userData);
    formik.resetForm();
    thunkApi.dispatch(SnackbarAction.success('Operacion exitosa, revise su bandeja de entrada o spam'));
  } catch (error) {
    thunkApi.dispatch(SnackbarAction.error((error as Error).message));
    return thunkApi.rejectWithValue((error as Error).message);
  }
});

export const confirmPasswordRecovery = createAsyncThunk<
  {},
  { userData: ConfirmRecoveryPasswordRequestDTO; formik: FormikProps<any> },
  { rejectValue: string }
>('user/confirm-password-recovery', async ({ userData, formik }, thunkApi) => {
  try {
    await userService.confirmRecoveryPassword(userData);
    formik.resetForm();
    thunkApi.dispatch(SnackbarAction.success('Operacion exitosa'));
  } catch (error) {
    thunkApi.dispatch(SnackbarAction.error((error as Error).message));
    return thunkApi.rejectWithValue((error as Error).message);
  }
});

export const updateUser = createAsyncThunk<
  {},
  { userData: UserUpdateRequestDTO; formik: FormikProps<any> },
  { rejectValue: string }
>('user/update', async ({ userData, formik }, thunkApi) => {
  try {
    await userService.updateUser(userData);
    formik.resetForm();
    thunkApi.dispatch(SnackbarAction.success('Operacion exitosa'));
  } catch (error) {
    thunkApi.dispatch(SnackbarAction.error((error as Error).message));
    return thunkApi.rejectWithValue((error as Error).message);
  }
});

export const removeUser = createAsyncThunk<
  {},
  { userData: RemoveRequestDTO; formik: FormikProps<any> },
  { rejectValue: string }
>('user/remove', async ({ userData, formik }, thunkApi) => {
  try {
    analyticsAdapter.trackEvent({
      action: AnalyticsEventName.USER_DROP_OUT,
      category: AnalyticsEventCategory.ECOMMERCE,
      params: {
        email: userData.email,
      },
    });
    await userService.remove(userData);
    thunkApi.dispatch(logOut());
    formik.resetForm();
    thunkApi.dispatch(SnackbarAction.success('Operacion exitosa, revise su bandeja de entrada'));
  } catch (error) {
    thunkApi.dispatch(SnackbarAction.error((error as Error).message));
    return thunkApi.rejectWithValue((error as Error).message);
  }
});

export const logOut = createAsyncThunk<{}>('user/logOut', async (_, thunkApi) => {
  await userService.logOut();
  thunkApi.dispatch(clearCart());
  StorageHandlerHelper.clear('token');
  StorageHandlerHelper.clear('cart_id');
});
